import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { View, Keyboard, InteractionManager, } from 'react-native';
import emoji from 'node-emoji';
import deepEqual from 'deep-equal';
import Animated, { useSharedValue, useAnimatedStyle, useAnimatedScrollHandler, } from 'react-native-reanimated';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Separator from 'design-system/Separator';
import { AppView } from 'design-system/AppView';
import SectionHeader from 'design-system/SectionHeader';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { KeyboardAvoidingButton } from 'design-system/KeyboardAvoidingButton';
import { useSortBySelector } from 'design-system/SortBySelector/useSortBySelector';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import useStyles from 'hooks/useStyles';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { setTags, editTagsTransactions, getTags } from 'actions';
import { isWeb } from '../../../constants';
import TagRow from '../components/tags/TagRow';
import TagsList from '../components/tags/TagsList';
import { selectFilteredUserTags } from '../selectors';
import useCheckTransactionsModal from '../hooks/useCheckTransactionsModal';
const hashtagRegex = require('hashtag-regex');
const placeholders = [
    emoji.emojify('#:moneybag:'),
    emoji.emojify('#:pizza:'),
    emoji.emojify('#:fries:'),
    emoji.emojify('#:hamburger:'),
    emoji.emojify('#:icecream:'),
    emoji.emojify('#:coffee:'),
    emoji.emojify('#:rocket:'),
    emoji.emojify('#:sushi:'),
    emoji.emojify('#:ramen:'),
    emoji.emojify('#:rice_ball:'),
];
const keyExtractor = (item) => item.label;
const AddTagsScreen = withAnimatedNavigationHeader(({ navigation, route }) => {
    const editData = route.params;
    const styles = useStyles(styleSet);
    const [, , isFetching, fetch] = useFetchOld(muteErrorAndResult);
    const dispatch = useAppDispatch();
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const flatListContentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const scrollY = useSharedValue(0);
    const [state, setState] = useState({
        tags: editData.type === 'single' ? editData.tags : [],
        text: '',
        error: false,
    });
    const { sortMethod, Button } = useSortBySelector('Transaction');
    const userTags = useAppSelector((state) => selectFilteredUserTags(state, sortMethod));
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            dispatch(getTags());
        });
    }, []);
    const getTagsToSave = useCallback(() => {
        const tagsToSave = state.tags;
        if (state.text.length > 0) {
            return [...tagsToSave, state.text.substring(1).replace(/\s/g, '')];
        }
        return tagsToSave;
    }, [state]);
    const openSimilarTransactions = useCallback(() => {
        if (editData.type === 'single') {
            const { transaction } = editData;
            const tagsToSave = getTagsToSave();
            if (isWeb) {
                navigation.navigateDeprecated('EditMultipleTransactionsTags', {
                    tags: tagsToSave,
                    transaction,
                    isSelectedAll: true,
                    keyBack: route.key,
                });
            }
            else {
                Modal.hide(() => {
                    navigation.navigateDeprecated('EditMultipleTransactionsTags', {
                        tags: tagsToSave,
                        transaction,
                        isSelectedAll: true,
                        keyBack: route.key,
                    });
                });
            }
        }
    }, [editData, getTagsToSave, route.key]);
    const saveTagsForTransaction = useCallback(() => {
        if (editData.type === 'single') {
            Modal.hide(async () => {
                const { tags, id } = editData;
                const tagsToSave = getTagsToSave();
                if (!deepEqual(tags, tagsToSave)) {
                    const saved = await fetch(setTags(id, tagsToSave));
                    // useFetchOld resolves null when the request fails
                    if (saved !== null) {
                        editData.onEditCallBack?.();
                    }
                }
                navigation.goBack();
            });
        }
    }, [editData, getTagsToSave]);
    const openCheckTransactionsModal = useCheckTransactionsModal(saveTagsForTransaction, openSimilarTransactions);
    const handleSave = useCallback(async () => {
        Keyboard.dismiss();
        if (editData.type === 'multi') {
            const tagsToSave = getTagsToSave();
            await fetch(editTagsTransactions(editData.ids, tagsToSave));
            navigation.goBack();
            editData.onEditFinished?.(tagsToSave);
        }
        else if (isWeb) {
            openSimilarTransactions();
        }
        else {
            openCheckTransactionsModal();
        }
    }, [
        editData,
        getTagsToSave,
        openCheckTransactionsModal,
        openSimilarTransactions,
    ]);
    const onChangeTags = useCallback((tags) => {
        setState((state) => ({ ...state, tags }));
    }, []);
    const onChangeText = (text) => {
        if (text.length === 1 && text.charAt(0) === ' ')
            return;
        if (text.length >= 20) {
            setState({ ...state, error: true });
            return;
        }
        // Escape string
        let newText = text.replace(/[.*+?^${}()|[\]\\]/g, '');
        if (newText.charAt(0) !== '#' && newText.length)
            newText = `#${text}`;
        const regex = hashtagRegex();
        if (!regex.exec(newText) &&
            !(newText.length === 1 && newText.charAt(0) === '#') &&
            newText.length)
            return;
        const parseWhen = [' '];
        if (state.tags &&
            state.tags.indexOf(newText.substring(1).slice(0, -1).replace(/\s/g, '')) !== -1 &&
            parseWhen.indexOf(newText.charAt(newText.length - 1)) > -1) {
            return;
        }
        const lastTyped = newText.charAt(newText.length - 1);
        if (lastTyped === ' ') {
            setState((state) => ({
                ...state,
                tags: [
                    ...state.tags,
                    newText.substring(1).slice(0, -1).replace(/\s/g, ''),
                ],
                text: '',
            }));
        }
        else {
            setState({
                ...state,
                text: newText.replace(/\s/g, ''),
                error: false,
            });
        }
    };
    const onTagPressed = useCallback((item) => {
        setState((state) => ({
            ...state,
            tags: [...state.tags, item],
            text: '',
        }));
    }, []);
    const renderItem = useCallback(({ item }) => {
        if (state.tags && state.tags.indexOf(item.label) > -1)
            return null;
        return <TagRow data={item} onPress={onTagPressed}/>;
    }, [onTagPressed, state.tags]);
    const { error } = state;
    const data = useMemo(() => {
        const stateTagMap = state.tags.reduce((acc, tag) => {
            acc[tag] = true;
            return acc;
        }, {});
        return userTags.filter((tag) => !stateTagMap[tag.label] &&
            (state.text ? tag.label.match(state.text.replace('#', '')) : true));
    }, [state.tags, state.text, userTags]);
    const handleScroll = useAnimatedScrollHandler((handle) => {
        scrollY.value = handle.contentOffset.y;
    }, []);
    const separatorAnimatedStyle = useAnimatedStyle(() => ({
        opacity: scrollY.value >= 1 ? 1 : 0,
    }), [scrollY]);
    const placeholder = useMemo(() => placeholders[Math.floor(Math.random() * placeholders.length)], []);
    return (<AppView withFrame={false}>
        <View style={paddingHorizontalStyle}>
          <View style={styles.scrollWrapper}>
            <TagsList tags={state.tags} text={state.text} onSubmitEditing={handleSave} onChange={onChangeTags} onChangeText={onChangeText}/>
          </View>
          {!!(!data.length || error) && (<View style={styles.hintContainer}>
              {!data.length && (<Text TextThin-14 centered style={styles.hintText}>
                  Add {placeholder} separated by space.
                </Text>)}
              {error ? (<Text TextThin-14 Red centered>
                  {emoji.emojify('Too many characters :warning:')}
                </Text>) : null}
            </View>)}
        </View>
        {!!data.length && (<>
            <View style={paddingHorizontalStyle}>
              <SectionHeader button={Button} horizontalMargins largeVerticalMargins>
                Your tags
              </SectionHeader>
            </View>
            <Animated.View style={separatorAnimatedStyle}>
              <Separator style={styles.separator}/>
            </Animated.View>
          </>)}
        <Animated.FlatList data={data} renderItem={renderItem} scrollEventThrottle={16} onScroll={handleScroll} keyExtractor={keyExtractor} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={flatListContentContainerStyle}/>
        <KeyboardAvoidingButton brand title="Save" onPress={handleSave} isFetching={isFetching}/>
      </AppView>);
}, ({ colors, route }) => ({
    title: 'transaction' in route.params ? 'Edit tags' : 'Add tags',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    headerRightBtn: {
        ...(isWeb && {
            marginRight: rem(16),
            paddingVertical: rem(8),
            paddingHorizontal: rem(16),
        }),
    },
    scrollWrapper: {
        height: rem(isWeb ? 232 : 150),
    },
    hintContainer: {
        marginTop: rem(10),
        marginBottom: rem(16),
    },
    hintText: {
        marginBottom: rem(2),
    },
    separator: {
        backgroundColor: colors.borders.divider,
    },
}));
export default AddTagsScreen;
